import { Bell, Check, Flag, Goal, ShieldQuestion, Trash2, TrendingDown, Trophy, UserPlus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AppNotification, NotificationKind } from '../lib/notifications.tsx';
import { useNotifications } from '../lib/notifications.tsx';

/* La cloche, à côté de l'avatar.
 *
 * Toujours présente, et non affichée seulement quand il y a quelque chose : une
 * icône qui apparaît et disparaît déplace ce qui l'entoure, et surtout elle
 * n'apprend à personne qu'elle existe. Le point coloré, lui, ne se pose que
 * s'il y a du non-lu.
 */

/* L'icone dit le SUJET, pas l'humeur : on reconnait de quoi il s'agit avant
 * d'avoir lu. Le drapeau va au parcours comme dans le menu ; le putting prend
 * la cible qui lui est propre. */
export const ICONS: Record<NotificationKind, typeof Bell> = {
    friend_added: UserPlus,
    putting_invite: Goal,
    registration_pending: ShieldQuestion,
    round_invite: Flag,
    round_finished: Trophy,
    handicap_improved: TrendingDown,
};

/** « il y a 3 min », « hier ». Une date complète serait exacte et illisible :
 *  ce qu'on veut savoir d'une notification, c'est si elle est fraîche. */
function ago(iso: string): string {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "à l'instant";
    if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
    if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
    const d = Math.floor(s / 86400);
    return d === 1 ? 'hier' : `il y a ${d} jours`;
}

export function NotificationBell() {
    const { notifications, unread, markRead, clear } = useNotifications();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const panel = useRef<HTMLDivElement>(null);

    // Fermeture au clic extérieur et à Échap : un panneau qu'on ne sait pas
    // fermer est pire que pas de panneau.
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e: MouseEvent): void => {
            if (panel.current && !panel.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const go = (n: AppNotification): void => {
        void markRead(n.id);
        setOpen(false);
        if (n.link) void navigate(n.link);
    };

    return (
        <div className="relative" ref={panel}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label={unread > 0 ? `Notifications, ${unread} non lue${unread > 1 ? 's' : ''}` : 'Notifications'}
                aria-expanded={open}
                className="btn btn-ghost btn-square relative"
            >
                <Bell size={22} />
                {unread > 0 && (
                    <span className="absolute right-1 top-1 flex min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-4 text-primary-content">
                        {unread > 9 ? '9+' : unread}
                    </span>
                )}
            </button>

            {/* Le panneau porte SES couleurs, il n'en hérite aucune.
                La cloche vit dans l'entête, et l'entête est sombre sur l'écran
                de jeu : le panneau posait donc du texte blanc hérité sur son
                propre fond pâle, et l'on ouvrait une bulle vide. Fond, encre
                et bordure sont écrits ici, une bonne fois — c'est la règle
                pour tout ce qui flotte au-dessus d'un fond qu'il ne choisit
                pas. */}
            {open && (
                <div className="absolute right-0 top-full z-50 mt-2 w-80 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-box border border-base-300 bg-base-100 text-base-content shadow-2xl">
                    <div className="flex items-center gap-1 px-3 py-2.5">
                        <h2 className="flex flex-1 items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em]"
                            style={{ color: 'var(--tuile-encre)' }}>
                            <Bell size={13} aria-hidden="true" style={{ color: 'var(--color-primary)' }} />
                            Notifications
                        </h2>
                        {unread > 0 && (
                            <button type="button" onClick={() => void markRead()}
                                className="flex items-center gap-1 rounded-field px-2 py-1 text-[11px] font-bold transition-colors hover:bg-base-200"
                                style={{ color: 'var(--color-primary)' }}>
                                <Check size={13} aria-hidden="true" /> Tout lire
                            </button>
                        )}
                        {notifications.length > 0 && (
                            <button type="button" onClick={() => void clear()}
                                aria-label="Tout effacer"
                                className="rounded-field p-1.5 transition-colors hover:bg-base-200"
                                style={{ color: 'var(--brique-bord)' }}>
                                <Trash2 size={13} aria-hidden="true" />
                            </button>
                        )}
                    </div>

                    <div className="max-h-[60dvh] overflow-y-auto border-t border-base-300">
                        {notifications.length === 0 ? (
                            <p className="px-4 py-8 text-center text-sm opacity-60">
                                Rien pour l’instant.
                            </p>
                        ) : notifications.map((n) => {
                            const Icon = ICONS[n.kind] ?? Bell;
                            /* Le NON-LU porte le sable, le lu reste sur le
                               papier. C'était une pastille de deux pixels au
                               bout de la ligne : au soleil, sur un téléphone
                               tenu d'une main, on ne la voyait pas. */
                            const neuf = !n.readAt;
                            return (
                                <button
                                    key={n.id}
                                    type="button"
                                    onClick={() => go(n)}
                                    style={neuf
                                        ? { backgroundColor: 'var(--tuile)', color: 'var(--tuile-encre)' }
                                        : undefined}
                                    className={`flex w-full items-start gap-3 border-b border-base-300 px-3 py-2.5 text-left transition-colors last:border-0 hover:brightness-95 ${
                                        neuf ? '' : 'opacity-60'
                                    }`}
                                >
                                    <span className="mt-0.5 shrink-0 rounded-field p-1.5"
                                        style={{
                                            backgroundColor: neuf ? 'var(--color-base-100)' : 'var(--tuile)',
                                            color: 'var(--color-primary)',
                                        }}>
                                        <Icon size={16} aria-hidden="true" />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-bold leading-[1.25]">{n.title}</span>
                                        {n.body && (
                                            <span className="mt-0.5 block text-xs opacity-75">{n.body}</span>
                                        )}
                                        <span className="mt-1 block text-[10px] font-bold uppercase tracking-[0.1em] opacity-55">
                                            {ago(n.createdAt)}
                                        </span>
                                    </span>
                                    {neuf && (
                                        <span className="mt-1.5 size-2 shrink-0 rounded-full"
                                            style={{ backgroundColor: 'var(--color-primary)' }} />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
